import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";
import { v4 as uuid } from "uuid";

import tvgConf from "@tvg/conf";
import mediator from "@tvg/mediator";
import BetCancel from "@tvg/desktop-bet/src/components/cancel-bet/index";
import { isSuccessBetCancel } from "@tvg/sh-lib-my-bets/utils/general";
import { safeJSONParse } from "@tvg/sh-lib-my-bets/utils/service";
import useMyBetsUpdateRaces from "@tvg/sh-lib-my-bets/hooks/useMyBetsUpdateRaces";
import {
  closeBetCancelModal,
  closeWagerCancellationRulesModal,
  openWagerCancellationRulesModal,
  openBetCancelModal
} from "@tvg/sh-lib-my-bets/redux/actions";
import { getResidenceState } from "@urp/store-selectors";
import {
  getBetCancel,
  getBetCancelIsOpen,
  getBetCancelIsLoadingBetCancelRequest,
  getIsWagerCancellationRulesModalOpen,
  getBetCancelLimitationsByState,
  getIsMyBetsOpen
} from "@tvg/sh-lib-my-bets/redux/selectors";
import {
  breakpoints,
  Modal,
  ToastMessageLink,
  useMediaQuery,
  ModalType
} from "@tvg/design-system";
import CancellationRulesModal, {
  Messages as RulesModalMessages
} from "../../../CancellationRulesModal";
import { closeModalBetGtmEvent } from "../../../../utils/gtm/betCancel";
import { BetCancelModalProps } from "../types";
import { addToast, removeToast } from "../../../../store/actions";

const BetCancelModal = ({
  betValue,
  wagerSelected,
  races,
  behgClient,
  qaLabel = "pp-cancel-bet-modal"
}: BetCancelModalProps) => {
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);
  const dispatch = useDispatch();
  const isDesktop = tvgConf().device === "desktop";
  const isOpen = useSelector(getBetCancelIsOpen);
  const isLoading = useSelector(getBetCancelIsLoadingBetCancelRequest);
  const userLocation = useSelector(getResidenceState);
  const betCancel = useSelector(getBetCancel);
  const cancelLimitationsByState = useSelector(getBetCancelLimitationsByState);
  const wagerCancellationModal = useSelector(
    getIsWagerCancellationRulesModalOpen
  );
  const myBetsIsOpen = useSelector(getIsMyBetsOpen);

  // @ts-ignore
  useMyBetsUpdateRaces({ dispatch, races });
  const betCancelResult = get(betCancel, "result");

  useEffect(() => {
    if (
      !myBetsIsOpen &&
      betCancelResult &&
      isSuccessBetCancel(betCancelResult)
    ) {
      const toastId = uuid();

      dispatch(
        addToast({
          id: toastId,
          variant: "informational",
          title: "",
          message: "Bet cancelled",
          hasDismissButton: true,
          qaLabel: "bet-cancelled-toast",
          onHide: () => dispatch(removeToast(toastId)),
          onDismissPress: () => dispatch(removeToast(toastId)),
          actionLink: (
            <ToastMessageLink
              variant="success"
              onPress={() => {
                dispatch(removeToast(toastId));
                dispatch(openWagerCancellationRulesModal({ hasBack: false }));
              }}
              qaLabel="delete-selection-notification"
            >
              See Rules
            </ToastMessageLink>
          )
        })
      );
    }
  }, [betCancelResult?.status, myBetsIsOpen]);

  const cancelRulesMessages = useMemo(
    () =>
      safeJSONParse({
        defaultValue: {},
        jsonToParse: `${cancelLimitationsByState}`
      }),
    [cancelLimitationsByState]
  );

  const onClose = () => {
    dispatch(closeBetCancelModal({ result: { status: "aborted" } }));
    closeModalBetGtmEvent();
  };

  const modalType = (isMobile ? "flexible" : "lightbox") as ModalType;

  const props = {
    title: isLoading ? "Processing Cancellation..." : "Confirm Cancellation",
    isOpen: isOpen && !myBetsIsOpen,
    onClose,
    onOverlayClick: onClose,
    hasBorderBottom: false,
    shouldCloseOnEsc: false,
    qaLabel,
    padding: 0,
    type: modalType
  };

  return (
    <>
      <Modal {...props}>
        <BetCancel behgClient={behgClient} isDesktop={isDesktop} />
      </Modal>

      <CancellationRulesModal
        isOpen={wagerCancellationModal.isOpen && !myBetsIsOpen}
        hasBack={wagerCancellationModal.hasBack}
        onClose={() => {
          dispatch(closeWagerCancellationRulesModal());
        }}
        userLocation={userLocation || ""}
        messages={cancelRulesMessages as RulesModalMessages}
        onBack={() => {
          dispatch(closeWagerCancellationRulesModal());
          setTimeout(() => {
            dispatch(
              openBetCancelModal(
                wagerSelected
                  ? {
                      bet: {
                        value: betValue,
                        wagers: [wagerSelected]
                      },
                      wager: wagerSelected
                    }
                  : undefined
              )
            );
            mediator.base.dispatch({ type: "OPEN_BET_CANCEL_MODAL" });
          }, 200);
        }}
      />
    </>
  );
};

export default BetCancelModal;
